import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { normalizeRole } from '../utils/auth'

// Load auth state from localStorage
const loadAuthFromStorage = () => {
  try {
    const savedAuth = localStorage.getItem('authState')
    if (savedAuth) {
      const parsedAuth = JSON.parse(savedAuth)
      return parsedAuth
    }
  } catch (error) {
  }
  return {
    isAuthenticated: false,
    user: null,
    role: null,
    permissions: [],
    loading: false,
    error: null,
    forgotPassword: {
      success: false,
      message: null
    }
  }
}

const initialState = loadAuthFromStorage()

// Async thunk for login API call
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, role }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/user-service/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          role
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }))
        return rejectWithValue(errorData || 'Login failed')
      }
      const data = await response.json()
      const user = data?.data;
      if (user && typeof user === 'object') {
        const incomingNdaUrl =
          data?.nda_url !== undefined
            ? data.nda_url
            : data?.data?.nda_url !== undefined
            ? data.data.nda_url
            : data?.data?.user?.nda_url !== undefined
            ? data.data.user.nda_url
            : data?.user?.nda_url;

        user.nda_url = incomingNdaUrl !== undefined ? incomingNdaUrl : (user.nda_url ?? null);
        if (user.user && typeof user.user === 'object') {
          user.user.nda_url = user.nda_url;
        }

        if (data?.kyc_status !== undefined && user.kyc_status === undefined) {
          user.kyc_status = data.kyc_status;
        }
        if (data?.data?.user?.kyc_status !== undefined && user.kyc_status === undefined) {
          user.kyc_status = data.data.user.kyc_status;
        }
        if (user.user && user.kyc_status !== undefined) {
          user.user.kyc_status = user.kyc_status;
        }
      }
      const userRole = data?.data?.user?.role || data?.user?.role || user?.role || data?.role;
      // Based on the login response format, permissions are directly in the response
      const permissions = data?.permissions || data?.data?.permissions || data?.user?.permissions || user?.permissions || [];

      // const permissions = data?.data?.permissions;

      return {
        user: user,
        role: userRole,
        permissions: permissions
      }
    } catch (error) {
    
      return rejectWithValue(error.message || 'Network error')
    }
  }
)

// Async thunk for signup API call
export const signup = createAsyncThunk(
  'auth/signup',
  async ({ firstName, lastName,phone, email, password, referralCode }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/user-service/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          phone,
          email,
          password,
          role: 3,
          referal_code: referralCode || ''
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Signup failed' }))
        return rejectWithValue(errorData.message || 'Signup failed')
      }

      const data = await response.json()
     
      // Handle different possible response structures
      const user = data.data?.user || data.user || data;
      const userRole = data.data?.user?.role || data.user?.role || user?.role || data.role;

      return {
        user: user,
        role: userRole
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Network error')
    }
  }
)


export const ResendMail = createAsyncThunk(
  "auth/ResendMail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/user-service/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data?.message || "Resend failed");
      }

      return data;

    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  }
);

// Async thunk for forgot password API call
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, role_id }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/user-service/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, role_id }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data?.message || 'Forgot password request failed');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Async thunk for verify OTP API call
export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/sms-service/sms/verify-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'andy-internal-api-key-2024-change-in-production',
          },
          body: JSON.stringify({ phone, otp }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data?.message || 'OTP verification failed');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Async thunk for logout API call
export const logoutAsync = createAsyncThunk(
  'auth/logoutAsync',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.user?.token;
      const refreshToken = state.auth.user?.refresh_token;

      if (!token) {
        return { localOnly: true };
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/user-service/logout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            refresh_token: refreshToken || ''
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data?.message || 'Logout failed');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);
// /user-service/resend-verification

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      state.isAuthenticated = true
      state.user = action.payload.user
      state.role = action.payload.role
      state.permissions = action.payload.permissions
      state.loading = false
      state.error = null
      // Save to localStorage
      localStorage.setItem('authState', JSON.stringify({
        isAuthenticated: true,
        user: action.payload.user,
        role: action.payload.role,
        permissions: action.payload.permissions
      }))
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.role = null
      state.permissions = []
      state.loading = false
      state.error = null

      // Clear from localStorage
      localStorage.removeItem('authState')
    },
    clearResendState: (state) => {
      state.resend = {
        email: null,
        message: null,
        status: null,
        lastSentAt: null
      };
    },
    clearError: (state) => {
      state.error = null
    },
    updateUserNdaUrl: (state, action) => {
      const url = action.payload;
      if (state.user) {
        state.user.nda_url = url;
        state.user.kyc_status = 'verified';
        if (state.user.user) {
          state.user.user.nda_url = url;
          state.user.user.kyc_status = 'verified';
        }
      }
      try {
        const savedAuth = localStorage.getItem('authState');
        if (savedAuth) {
          const parsed = JSON.parse(savedAuth);
          if (parsed?.user) {
            parsed.user.nda_url = url;
            parsed.user.kyc_status = 'verified';
            if (parsed.user.user) {
              parsed.user.user.nda_url = url;
              parsed.user.user.kyc_status = 'verified';
            }
            localStorage.setItem('authState', JSON.stringify(parsed));
          }
        }
      } catch {
        // ignore storage parse error
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        const normalizedRole = normalizeRole(action.payload.role)
        state.isAuthenticated = true
        state.user = action.payload.user
        state.role = normalizedRole
        state.permissions = action.payload.permissions
        state.loading = false
        state.error = null
        localStorage.setItem('authState', JSON.stringify({
          isAuthenticated: true,
          user: action.payload.user,
          role: normalizedRole,
          permissions: action.payload.permissions
        }))
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.user = null
        state.role = null
      })
      .addCase(signup.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.isAuthenticated = true
        state.user = action.payload.user
        state.role = action.payload.role
        state.loading = false
        state.error = null
        // Save to localStorage
        localStorage.setItem('authState', JSON.stringify({
          isAuthenticated: true,
          user: action.payload.user,
          role: action.payload.role
        }))
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.user = null
        state.role = null
      })
      .addCase(ResendMail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(ResendMail.fulfilled, (state, action) => {
        state.loading = false;

        state.resend = {
          email: action.payload.email,
          message: action.payload.message,
          status: action.payload.status,
          lastSentAt: action.payload.time
        };

        // ✅ Persist resend data
        localStorage.setItem(
          "authState",
          JSON.stringify({
            ...state,
            loading: false,
            error: null
          })
        );
      })
      .addCase(ResendMail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.forgotPassword = {
          success: false,
          message: null
        };
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle different response structures: data.message, message, or default
        const responseMessage = action.payload?.data?.message || 
                                action.payload?.message || 
                                'Password reset link sent successfully';
        state.forgotPassword = {
          success: true,
          message: responseMessage,
          status: action.payload?.status || action.payload?.data?.status || 'success'
        };
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.forgotPassword = {
          success: false,
          message: null
        };
      })
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.permissions = [];
        state.loading = false;
        state.error = null;
        state.forgotPassword = {
          success: false,
          message: null
        };
        state.resend = {
          email: null,
          message: null,
          status: null,
          lastSentAt: null
        };
        // Clear from localStorage
        localStorage.removeItem('authState');
      })
      .addCase(logoutAsync.rejected, (state, action) => {
        // Even if API fails, clear local state
        state.isAuthenticated = false;
        state.user = null;
        state.role = null;
        state.permissions = [];
        state.loading = false;
        state.error = action.payload;
        state.forgotPassword = {
          success: false,
          message: null
        };
        state.resend = {
          email: null,
          message: null,
          status: null,
          lastSentAt: null
        };
        // Clear from localStorage
        localStorage.removeItem('authState');
      });

  }
})

export const { loginSuccess, logout, clearError, clearResendState, updateUserNdaUrl } = authSlice.actions
export default authSlice.reducer
